import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { TalkRoomContent } from '../../types';

interface ContentsTabProps {
  roomId: string;
  onJumpToMessage?: (messageId: string) => void;
}

export default function ContentsTab({ roomId, onJumpToMessage }: ContentsTabProps) {
  const [contents, setContents] = useState<TalkRoomContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'image' | 'pdf' | 'document'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  useEffect(() => {
    fetchContents();
  }, [roomId, filter]);

  const fetchContents = async () => {
    setLoading(true);
    try {
      const query = filter !== 'all' ? `?type=${filter}` : '';
      const data = await api.get<{ items: TalkRoomContent[] }>(`/api/talkrooms/${roomId}/contents${query}`);
      setContents(data.items || []);
    } catch (error) {
      console.error('Failed to fetch contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedContents = [...contents].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.fileName.localeCompare(b.fileName);
      case 'size':
        return (b.fileSize || 0) - (a.fileSize || 0);
      case 'date':
      default:
        if (!a.uploadedAt) return 1;
        if (!b.uploadedAt) return -1;
        return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
    }
  });

  const handleItemClick = (item: TalkRoomContent) => {
    // 画像の場合はプレビュー
    const isImage = item.fileType?.startsWith('image/') || 
      /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(item.fileName);
    
    if (isImage) {
      setPreviewImage({ url: item.fileUrl, name: item.fileName });
      return;
    }

    // メッセージ添付の場合はジャンプ機能を呼び出す
    if (item.source === 'message' && onJumpToMessage) {
      onJumpToMessage(item.sourceId);
      return;
    }

    // それ以外は直接開く
    window.open(item.fileUrl, '_blank');
  };

  const getSourceLabel = (source: 'message' | 'folder' | 'note') => {
    switch (source) {
      case 'message':
        return { label: 'メッセージ', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
      case 'folder':
        return { label: 'フォルダ', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' };
      case 'note':
        return { label: 'ノート', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
    }
  };

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isImage = (item: TalkRoomContent) => {
    const type = item.fileType?.toLowerCase() || '';
    const name = item.fileName.toLowerCase();
    return type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/.test(name);
  };

  const getFileIcon = (item: TalkRoomContent) => {
    const type = item.fileType?.toLowerCase() || '';
    const name = item.fileName.toLowerCase();

    if (type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/.test(name)) {
      return (
        <div className="w-12 h-12 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
          <img
            src={item.fileUrl}
            alt={item.fileName}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      );
    }

    if (type === 'application/pdf' || name.endsWith('.pdf')) {
      return (
        <div className="w-12 h-12 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }

    if (/\.(doc|docx)$/.test(name) || type.includes('word')) {
      return (
        <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
    }

    if (/\.(xls|xlsx|csv)$/.test(name) || type.includes('excel') || type.includes('spreadsheet')) {
      return (
        <div className="w-12 h-12 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
    }

    return (
      <div className="w-12 h-12 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* ヘッダー */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            コンテンツ
            <span className="ml-2 text-sm font-normal text-gray-500">({sortedContents.length}件)</span>
          </h3>
        </div>

        {/* フィルタとソート */}
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            {(['all', 'image', 'pdf', 'document'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs ${
                  filter === f
                    ? 'bg-blue-500 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {f === 'all' ? 'すべて' : f === 'image' ? '画像' : f === 'pdf' ? 'PDF' : 'ドキュメント'}
              </button>
            ))}
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
            className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            <option value="date">日時順</option>
            <option value="name">名前順</option>
            <option value="size">サイズ順</option>
          </select>
        </div>
      </div>

      {/* コンテンツ一覧 */}
      <div className="flex-1 overflow-y-auto p-4">
        {sortedContents.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-12">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p>コンテンツがありません</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedContents.map((item) => {
              const sourceInfo = getSourceLabel(item.source);
              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="w-full flex items-center gap-3 p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 text-left transition-colors"
                >
                  {getFileIcon(item)}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{item.fileName}</p>
                      <span className={`px-1.5 py-0.5 text-[10px] rounded ${sourceInfo.color}`}>
                        {sourceInfo.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{formatFileSize(item.fileSize)}</span>
                      <span>{item.uploadedByName || '-'}</span>
                      <span>{formatDate(item.uploadedAt)}</span>
                    </div>
                  </div>

                  {item.source === 'message' && onJumpToMessage && (
                    <div className="flex-shrink-0 text-blue-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 画像プレビューモーダル */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={previewImage.url}
              alt={previewImage.name}
              className="max-w-full max-h-[80vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-2 text-sm">{previewImage.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
